import { type ReactNode, useId } from "react";
import styles from "./DangerZone.module.css";

type DangerAction = {
  label: string;
  description: ReactNode;
  buttonLabel: string;
  onAction: () => void;
  disabled?: boolean;
  disabledReason?: string | null;
};

type DangerZoneProps =
  | {
      heading: string;
      actions: DangerAction[];
      children?: never;
      buttonLabel?: never;
      onAction?: never;
      disabled?: never;
      disabledReason?: never;
    }
  | {
      heading: string;
      children: ReactNode;
      buttonLabel: string;
      onAction: () => void;
      disabled?: boolean;
      disabledReason?: string | null;
      actions?: never;
    };

export function DangerZone(props: DangerZoneProps) {
  const { heading } = props;
  const reasonId = useId();

  if (props.actions) {
    return (
      <section className={styles.zone}>
        <h2 className={styles.heading}>{heading}</h2>
        {props.actions.map(action => (
          <div key={action.label} className={styles.body}>
            <div className={styles.description}>
              {action.description}
              {action.disabledReason && (
                <span id={`${reasonId}-${action.label}`} className={styles.reason}>
                  {action.disabledReason}
                </span>
              )}
            </div>
            <button
              type="button"
              className={styles.button}
              onClick={action.onAction}
              disabled={(action.disabled ?? false) || !!action.disabledReason}
              aria-describedby={action.disabledReason ? `${reasonId}-${action.label}` : undefined}
            >
              {action.buttonLabel}
            </button>
          </div>
        ))}
      </section>
    );
  }

  return (
    <section className={styles.zone}>
      <h2 className={styles.heading}>{heading}</h2>
      <div className={styles.body}>
        <div className={styles.description}>
          {props.children}
          {props.disabledReason && (
            <span id={reasonId} className={styles.reason}>
              {props.disabledReason}
            </span>
          )}
        </div>
        <button
          type="button"
          className={styles.button}
          onClick={props.onAction}
          disabled={(props.disabled ?? false) || !!props.disabledReason}
          aria-describedby={props.disabledReason ? reasonId : undefined}
        >
          {props.buttonLabel}
        </button>
      </div>
    </section>
  );
}
